import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "rm_cookie_consent";

export function getCookieConsent() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) setVisible(true);
  }, []);

  const save = (data) => {
    const payload = {
      ...data,
      timestamp: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    setVisible(false);
    // Notify listeners (e.g. to init analytics)
    window.dispatchEvent(new CustomEvent("rm-consent-change", { detail: payload }));
  };

  const acceptAll = () =>
    save({ status: "accepted", categories: { necessary: true, analytics: true, marketing: true } });
  const rejectAll = () =>
    save({ status: "rejected", categories: { necessary: true, analytics: false, marketing: false } });
  const saveCustom = () =>
    save({ status: "custom", categories: prefs });

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-50 p-4 md:p-6"
      role="dialog"
      aria-label="Zgoda na pliki cookie"
    >
      <div className="max-w-5xl mx-auto rm-card p-6 md:p-7 shadow-2xl border border-slate-200">
        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-xl bg-[#1f3a8a]/10 text-[#1f3a8a] items-center justify-center flex-shrink-0">
            <Cookie size={22} />
          </div>
          <div className="flex-grow min-w-0">
            <h3 className="font-display font-bold text-lg text-[#0f172a]">
              Ta strona używa plików cookie
            </h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              Wykorzystujemy pliki cookie oraz podobne technologie w celu zapewnienia prawidłowego działania strony,
              analizy ruchu (statystyki) oraz wyświetlania spersonalizowanych treści marketingowych. Możesz zaakceptować
              wszystkie pliki, odrzucić wszystkie (poza niezbędnymi) lub wybrać preferencje. Zgodę możesz w każdej chwili
              wycofać. Więcej informacji w{" "}
              <Link to="/polityka-cookies" className="text-[#1f3a8a] underline font-semibold">
                Polityce plików cookie
              </Link>{" "}
              i{" "}
              <Link to="/polityka-prywatnosci" className="text-[#1f3a8a] underline font-semibold">
                Polityce prywatności
              </Link>.
            </p>

            {showDetails && (
              <div className="mt-5 space-y-3" data-testid="cookie-details">
                <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-not-allowed opacity-90">
                  <input type="checkbox" checked disabled className="mt-1" />
                  <div>
                    <div className="font-semibold text-sm text-slate-800">Niezbędne (zawsze aktywne)</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Konieczne do działania strony, obsługi formularzy i sesji użytkownika. Bez nich strona nie zadziała.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    className="mt-1"
                    data-testid="cookie-analytics-toggle"
                  />
                  <div>
                    <div className="font-semibold text-sm text-slate-800">Analityczne</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Pomagają zrozumieć, jak korzystasz ze strony (Google Analytics, statystyki wizyt). Dane
                      zanonimizowane.
                    </div>
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100">
                  <input
                    type="checkbox"
                    checked={prefs.marketing}
                    onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                    className="mt-1"
                    data-testid="cookie-marketing-toggle"
                  />
                  <div>
                    <div className="font-semibold text-sm text-slate-800">Marketingowe / reklamowe</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Wykorzystywane do wyświetlania dopasowanych reklam i mierzenia skuteczności kampanii (Meta Pixel,
                      Google Ads).
                    </div>
                  </div>
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-5">
              <button
                data-testid="cookie-accept-all"
                onClick={acceptAll}
                className="rm-btn-primary text-sm px-5 py-2.5"
              >
                Akceptuję wszystkie
              </button>
              <button
                data-testid="cookie-reject-all"
                onClick={rejectAll}
                className="text-sm px-5 py-2.5 rounded-full border border-slate-300 text-slate-700 font-bold hover:bg-slate-50 transition"
              >
                Odrzucam
              </button>
              {!showDetails ? (
                <button
                  data-testid="cookie-customize"
                  onClick={() => setShowDetails(true)}
                  className="text-sm px-5 py-2.5 rounded-full text-[#1f3a8a] font-semibold hover:bg-slate-50 transition"
                >
                  Ustawienia
                </button>
              ) : (
                <button
                  data-testid="cookie-save-custom"
                  onClick={saveCustom}
                  className="text-sm px-5 py-2.5 rounded-full border border-[#1f3a8a] text-[#1f3a8a] font-bold hover:bg-[#1f3a8a] hover:text-white transition"
                >
                  Zapisz wybrane ustawienia
                </button>
              )}
            </div>
          </div>
          <button
            data-testid="cookie-close"
            onClick={rejectAll}
            aria-label="Zamknij (odrzuca wszystkie opcjonalne)"
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
